<script setup lang="ts">
import { ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Tag } from '@lucide/vue';
import type { TipoDocumentoClienteVista } from '@evolfin/shared';
import { EvolfinApiError } from '@/api/client';
import { useAvvisi } from '@/composables/useAvvisi';
import { useInvalidation } from '@/composables/useInvalidation';
import { settingsKeys } from '../query-keys';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import StatoCaricamento from '@/components/ui/StatoCaricamento.vue';
import StatoVuoto from '@/components/ui/StatoVuoto.vue';
import FormTipoDocumentoDialog from '../components/FormTipoDocumentoDialog.vue';
import NavImpostazioni from '../components/NavImpostazioni.vue';
import {
  disattivaTipoDocumentoCliente,
  listaTipiDocumentoCliente,
  riattivaTipoDocumentoCliente,
  riordinaTipiDocumentoCliente,
} from '../api';

// Pagina di sola gestione: senza il privilegio il router la nasconde.
definePage({
  meta: { titolo: 'Tipi di documento', privilegi: ['can_manage_tipi_documento_cliente'] },
});

const avvisi = useAvvisi();
// La stessa lookup è letta dalla scheda documenti del cliente: si invalida il
// mondo, non questa lista, altrimenti là resterebbe l'elenco di prima.
const invalidate = useInvalidation('impostazioni');

const { data: tipi, isLoading } = useQuery({
  queryKey: settingsKeys.clientDocumentTypes(),
  queryFn: listaTipiDocumentoCliente,
});

const dialogAperto = ref(false);
const tipoInModifica = ref<TipoDocumentoClienteVista | null>(null);
const azioneInCorso = ref<number | null>(null);

function apriCreazione(): void {
  tipoInModifica.value = null;
  dialogAperto.value = true;
}
function apriModifica(t: TipoDocumentoClienteVista): void {
  tipoInModifica.value = t;
  dialogAperto.value = true;
}

async function toggleAttivo(t: TipoDocumentoClienteVista): Promise<void> {
  azioneInCorso.value = t.id;
  try {
    if (t.attivo) {
      await disattivaTipoDocumentoCliente(t.id);
      avvisi.successo('Disattivato: non comparirà più nel picker "Aggiungi documento"');
    } else {
      await riattivaTipoDocumentoCliente(t.id);
      avvisi.successo('Riattivato');
    }
    await invalidate();
  } catch (errore) {
    const messaggio =
      errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Errore', messaggio);
  } finally {
    azioneInCorso.value = null;
  }
}

async function sposta(indice: number, delta: -1 | 1): Promise<void> {
  const righe = tipi.value ?? [];
  const nuovoIndice = indice + delta;
  if (nuovoIndice < 0 || nuovoIndice >= righe.length) return;

  const ordinati = [...righe];
  [ordinati[indice], ordinati[nuovoIndice]] = [ordinati[nuovoIndice], ordinati[indice]];

  try {
    await riordinaTipiDocumentoCliente(ordinati.map((t) => t.id));
    await invalidate();
  } catch (errore) {
    const messaggio = errore instanceof EvolfinApiError ? errore.message : 'Riordino non riuscito';
    avvisi.errore('Errore', messaggio);
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <header class="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Impostazioni</p>
        <h1 class="mt-1 font-display text-[26px] leading-tight font-semibold">Tipi di documento</h1>
        <p class="mt-0.5 text-[13px] text-ink-secondary">
          La lista proposta nel picker "Aggiungi documento" della scheda cliente.
        </p>
      </div>
      <PulsanteAzione @click="apriCreazione">
        Nuovo tipo
        <template #icona><Plus :size="16" :stroke-width="2" /></template>
      </PulsanteAzione>
    </header>

    <NavImpostazioni />

    <StatoCaricamento v-if="isLoading" />

    <StatoVuoto
      v-else-if="!tipi?.length"
      :icona="Tag"
      titolo="Nessun tipo di documento"
      descrizione="Crea la prima voce della lista per popolare il picker sui documenti cliente."
    />

    <ul v-else class="card flex flex-col divide-y divide-line-soft overflow-hidden">
      <li
        v-for="(t, indice) in tipi"
        :key="t.id"
        class="flex items-center gap-3 px-4 py-3"
        :class="{ 'opacity-55': !t.attivo }"
      >
        <div class="flex shrink-0 flex-col">
          <button
            type="button"
            class="rounded-sm p-0.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink disabled:pointer-events-none disabled:opacity-30"
            title="Sposta su"
            :disabled="indice === 0"
            @click="sposta(indice, -1)"
          >
            <ArrowUp :size="14" :stroke-width="2" />
          </button>
          <button
            type="button"
            class="rounded-sm p-0.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink disabled:pointer-events-none disabled:opacity-30"
            title="Sposta giù"
            :disabled="indice === tipi.length - 1"
            @click="sposta(indice, 1)"
          >
            <ArrowDown :size="14" :stroke-width="2" />
          </button>
        </div>

        <div class="min-w-0 flex-1">
          <p class="truncate text-[13.5px] font-medium">{{ t.nome }}</p>
          <div
            v-if="!t.attivo || t.richiestoPerProgetto || t.richiedeEtichetta"
            class="mt-1 flex flex-wrap items-center gap-1.5"
          >
            <span v-if="!t.attivo" class="text-[12px] text-ink-muted">Disattivato</span>
            <span
              v-if="t.richiestoPerProgetto"
              title="Richiesto per aprire un progetto"
              class="rounded-full bg-[var(--surface-accent-soft)] px-2.5 py-0.5 text-[11.5px] font-semibold text-[var(--color-primary)]"
            >
              Richiesto
            </span>
            <span
              v-if="t.richiedeEtichetta"
              class="rounded-full bg-[var(--surface-accent-soft)] px-2.5 py-0.5 text-[11.5px] font-semibold text-[var(--color-primary)]"
            >
              Richiede etichetta
            </span>
          </div>
        </div>

        <div class="flex shrink-0 items-center gap-1">
          <button
            type="button"
            class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
            title="Modifica"
            @click="apriModifica(t)"
          >
            <Pencil :size="16" :stroke-width="1.8" />
          </button>
          <PulsanteAzione
            variante="silenzioso"
            :caricamento="azioneInCorso === t.id"
            @click="toggleAttivo(t)"
          >
            {{ t.attivo ? 'Disattiva' : 'Riattiva' }}
          </PulsanteAzione>
        </div>
      </li>
    </ul>

    <FormTipoDocumentoDialog
      v-model="dialogAperto"
      :tipo="tipoInModifica"
      @salvato="invalidate()"
    />
  </div>
</template>
